import posthog from "posthog-js";
import { axios_server } from "..";
import config from "../../config";
import {
  LoginDataType,
  LookupType,
  RegisterDataType,
  UserDataType,
} from "../../types/auth.type";

const BASE_URL = config.AUTH_API_URL;

function buildUrl(path: string) {
  return `${BASE_URL}${path}`;
}

export const endpoints = {
  login: buildUrl("/beta/login"),
  verifyEmail: buildUrl("/beta/verify-email"),
  register: buildUrl("/beta/signup"),
  getUser: buildUrl("/user/me"),
  refreshToken: buildUrl("/refreshtoken"),
  updateUser: buildUrl("/user/update"),
  lookup: buildUrl("/beta/lookup"),
  notification: buildUrl("/user/notifications"),
  build: buildUrl("/user/builds"),
  onboard: buildUrl("/user/onboard"),
  overview: buildUrl("/user/overview"),
};

export const authorizeApp = async (userId: string) => {
  const response = await axios_server.post(
    "integrate/v1/oauth-integration/authorize",
    { userId },
  );
  return response.data;
};

export const oAuthIntegration = async (token: string) => {
  const response = await axios_server.get("/integrate/v1/oauth-integration/", {
    headers: { "X-Brimble-Auth": token },
  });
  return response.data;
};

const userLookup = (data: LookupType) =>
  new Promise((resolve, reject) => {
    axios_server
      .get(
        `${endpoints.lookup}?${
          data.email ? `email=${data.email}` : `username=${data.username}`
        }`,
      )
      .then((res) => (res.status === 200 ? resolve(res) : reject(res)))
      .catch((error) => reject(error.response));
  });

const login = (data: LoginDataType) => {
  posthog.capture("Login", data);
  return new Promise((resolve, reject) => {
    axios_server
      .post(endpoints.login, data)
      .then((res) => resolve(res))
      .catch((error) => reject(error));
  });
};

const register = (data: RegisterDataType) =>
  new Promise((resolve, reject) => {
    axios_server
      .post(endpoints.register, data)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => reject(error));
  });

const verifyEmailAddress = async (email: string, access_code: string) => {
  const response: any = await axios_server.post(endpoints.verifyEmail, {
    email,
    access_code,
  });
  return response;
};

const refreshToken = async (refresh_token: string) => {
  const response = await axios_server.post(endpoints.refreshToken, {
    refresh_token,
  });
  return response;
};

const handleUpdateUser = async (data: Partial<UserDataType>) => {
  const response = await axios_server.put(endpoints.updateUser, data);
  return response;
};

const handleLogout = async () => {
  return await Promise.all([
    axios_server.post("/v1/logout"),
    axios_server.post(`${config.AUTH_API_URL}/beta/logout`),
  ]);
};

const addNewCard = (paymentChannel: any) =>
  new Promise((resolve, reject) => {
    const payload = {
      type: "ADD_CARD",
      payment_channel: paymentChannel,
    };
    axios_server
      .post(config.PAYMENT_BASE_URL + "/payment/initialize/payment", payload)
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          const paymentLink = res.data.data.payment_link;
          posthog.capture("Add Card", payload);
          window.open(paymentLink, "_blank");
          resolve(res);
        } else {
          reject(res);
        }
      })
      .catch((error) => reject(error.response))
      .catch((err) => {
        reject(err);
      });
  });

const notificationHandler = (data: { mute: boolean; email: boolean }) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .post(endpoints.notification, data)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

const buildHandler = (data: { build_disabled: boolean }, teamId?: string) =>
  new Promise<any>((resolve, reject) => {
    const url = teamId
      ? `${endpoints.build}?team_id=${teamId}`
      : endpoints.build;

    axios_server
      .put(url, data)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

const onboardHandler = (data: { user?: boolean; team?: boolean }) =>
  new Promise<any>((resolve, reject) => {
    axios_server
      .post(endpoints.onboard, data)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });

const userOverview = () => {
  return new Promise((resolve, reject) => {
    axios_server
      .get(endpoints.overview)
      .then((res) => resolve(res))
      .catch((error) => reject(error));
  });
};

export {
  login,
  register,
  verifyEmailAddress,
  refreshToken,
  handleUpdateUser,
  userLookup,
  handleLogout,
  addNewCard,
  notificationHandler,
  onboardHandler,
  buildHandler,
  userOverview,
};
